import { useEffect, useState, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ShoppingCart, Heart, Star, Minus, Plus } from 'lucide-react';

const ProductDetail = () => {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProduct();
    fetchReviews();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/${id}`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleAddToCart = async () => {
    if (!user) {
      toast.error('Please login to add items to cart');
      return;
    }

    try {
      await axios.post(`${API}/cart/add`, {
        product_id: product.id,
        quantity: quantity,
        price: product.price
      });
      toast.success('Added to cart!');
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add to cart');
    }
  };

  const handleAddToWishlist = async () => {
    if (!user) {
      toast.error('Please login to add items to wishlist');
      return;
    }

    try {
      await axios.post(`${API}/wishlist/add/${product.id}`);
      toast.success('Added to wishlist!');
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      toast.error('Failed to add to wishlist');
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to submit a review');
      return;
    }

    try {
      await axios.post(`${API}/reviews`, {
        product_id: product.id,
        rating,
        comment
      });
      toast.success('Review submitted!');
      setComment('');
      setRating(5);
      fetchReviews();
      fetchProduct();
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="container mx-auto px-4 py-20 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-500 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="container mx-auto px-4 py-20 text-center">
          <p className="text-xl text-gray-600">Product not found</p>
          <Link to="/products">
            <Button className="mt-4 btn-primary text-white">Back to Products</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white">
      <Navbar />
      
      <div className="container mx-auto px-4 py-12">
        {/* Product Details */}
        <div className="grid lg:grid-cols-2 gap-12 mb-16" data-testid="product-detail">
          {/* Image */}
          <div className="rounded-3xl overflow-hidden shadow-2xl bg-white p-8">
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-auto object-cover rounded-2xl"
              data-testid="product-image"
            />
          </div>

          {/* Info */}
          <div>
            <h1 className="text-4xl font-bold mb-4" data-testid="product-name">{product.name}</h1>
            
            <div className="flex items-center gap-2 mb-6">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-5 h-5 ${i < Math.floor(product.rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
                  />
                ))}
              </div>
              <span className="text-gray-600">({product.reviews_count} reviews)</span>
            </div>

            <div className="flex items-baseline gap-4 mb-6">
              <span className="text-4xl font-bold text-pink-600" data-testid="product-price">
                ₹{product.price}
              </span>
              {product.original_price && (
                <span className="text-2xl text-gray-400 line-through">
                  ₹{product.original_price}
                </span>
              )}
            </div>

            <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-testid="product-description">
              {product.description}
            </p>

            <div className="bg-white rounded-2xl p-6 shadow-lg mb-6">
              <div className="flex items-center gap-4 mb-6">
                <label className="font-semibold">Quantity:</label>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    data-testid="decrease-quantity"
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="text-xl font-semibold w-12 text-center" data-testid="quantity-display">{quantity}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setQuantity(quantity + 1)}
                    data-testid="increase-quantity"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="flex gap-4">
                <Button
                  className="flex-1 btn-primary text-white py-6 text-lg rounded-full"
                  onClick={handleAddToCart}
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingCart className="mr-2 h-5 w-5" />
                  Add to Cart
                </Button>
                <Button
                  variant="outline"
                  className="py-6 px-6 rounded-full border-2 border-pink-500 text-pink-600 hover:bg-pink-50"
                  onClick={handleAddToWishlist}
                  data-testid="add-to-wishlist-btn"
                >
                  <Heart className="h-5 w-5" />
                </Button>
              </div>
            </div>

            <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-2xl p-6">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Category:</span> {product.category}
              </p>
              <p className="text-sm text-gray-700 mt-2">
                <span className="font-semibold">Stock:</span> {product.stock} available
              </p>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="bg-white rounded-3xl shadow-xl p-8">
          <h2 className="text-3xl font-bold mb-8" data-testid="reviews-heading">Customer Reviews</h2>

          {/* Submit Review */}
          {user && (
            <form onSubmit={handleSubmitReview} className="mb-12 bg-pink-50 rounded-2xl p-6" data-testid="review-form">
              <h3 className="text-xl font-semibold mb-4">Write a Review</h3>
              <div className="mb-4">
                <label className="block mb-2 font-medium">Rating</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      data-testid={`star-${star}`}
                    >
                      <Star
                        className={`w-8 h-8 cursor-pointer ${star <= rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <div className="mb-4">
                <label className="block mb-2 font-medium">Comment</label>
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Share your experience..."
                  rows={4}
                  required
                  data-testid="review-comment"
                />
              </div>
              <Button type="submit" className="btn-primary text-white" data-testid="submit-review-btn">
                Submit Review
              </Button>
            </form>
          )}

          {/* Reviews List */}
          <div className="space-y-6" data-testid="reviews-list">
            {reviews.length === 0 ? (
              <p className="text-gray-600">No reviews yet. Be the first to review!</p>
            ) : (
              reviews.map((review) => (
                <div key={review.id} className="border-b pb-6" data-testid="review-item">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold">{review.user_name}</span>
                    <span className="text-sm text-gray-500">
                      {new Date(review.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${i < review.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
                      />
                    ))}
                  </div>
                  <p className="text-gray-700">{review.comment}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProductDetail;